import { ExpandMoreRounded } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { Controller, UseFormReturn } from "react-hook-form";
import { Autocomplete, MetricTable, UserAutocomplete } from "src/components";
import { useGetTeams, UserDto } from "src/http";
import { ITeamSummary } from "src/models";
import { ConnectObjectType } from "../../constants";
import { SmallInputLabel } from "../SmallInputLabel";
import { EditMetricDatasets } from "./EditMetricDatasets";
import { useSession } from "src/hooks";

interface ICreateEditMetricFormState {
  title: string;
  description: string;
  assignedTeam: ITeamSummary | null;
  reportingUser: UserDto | null;
  sortOrder: string;
  datasets: ICreateEditMetricFormStateDataset[];
  annotations: ICreateEditMetricFormStateAnnotation[];
}

interface ICreateEditMetricFormStateDataset {
  guid: string;
  title: string;
  displayAsBar: boolean;
  dotted: boolean;
  color: string;
  data: ICreateEditMetricFormStateData[];
  sortOrder: string;
}

interface ICreateEditMetricFormStateData {
  rowGuid: string;
  date: DateTime;
  value: number | null;
}

interface ICreateEditMetricFormStateAnnotation {
  rowGuid: string;
  text: string | null;
}

interface IProps {
  form: UseFormReturn<ICreateEditMetricFormState>;
}

const CreateEditMetricForm = (props: IProps) => {
  const { userId } = useSession();
  const { isLoading: isLoadingTeams, data: teams } = useGetTeams(userId);
  const { formState, control } = props.form;
  const theme = useTheme();

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    let aMembership = a.isAdministrator || a.isMember;
    let bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  }

  return (
    <Stack spacing={1} width={"100%"}>
      <Stack spacing={0.25}>
        <SmallInputLabel>Title *</SmallInputLabel>
        <Controller
          name="title"
          control={control}
          rules={{ required: { message: "Title is required", value: true } }}
          render={({ field }) => (
            <TextField
              {...field}
              error={formState.errors["title"] !== undefined}
              helperText={formState.errors["title"]?.message}
            />
          )}
        />
      </Stack>
      <Stack spacing={0.25}>
        <SmallInputLabel>Description</SmallInputLabel>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              helperText={formState.errors["description"]?.message}
              onFocus={(e) => e.target.setSelectionRange(0, e.target.value.length)}
            />
          )}
        />
      </Stack>
      <Stack spacing={1} direction="row" sx={{ width: "100%" }}>
        <Stack spacing={0.25} sx={{ width: "50%" }}>
          <SmallInputLabel>Team *</SmallInputLabel>
          <Controller
            name="assignedTeam"
            control={control}
            rules={{ required: "Metric must be assigned to a team" }}
            render={({ field }) => (
              <Autocomplete
                isLoading={isLoadingTeams}
                disabled={isLoadingTeams}
                options={teams ? [...teams].sort(sortFunction) : []}
                groupBy={(option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")}
                getOptionLabel={(option) => option.name}
                renderGroup={(group) => (
                  <Box key={group.key} sx={{ pb: 0.25 }}>
                    <Stack
                      spacing={0.2}
                      sx={{
                        px: 0.5,
                        pt: 0.75,
                        position: "sticky",
                        top: 0,
                        backgroundColor: theme.palette.common.white,
                      }}
                    >
                      <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                      <Divider />
                    </Stack>
                    {group.children}
                  </Box>
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option.slug} component="li">
                    <Stack>
                      <Typography>{option.name}</Typography>
                      {!option.isAdministrator && !option.isMember && (
                        <Typography variant="caption">You are not a member or administrator of this team</Typography>
                      )}
                    </Stack>
                  </MenuItem>
                )}
                onChange={(_, option) => field.onChange(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a team..."
                    error={formState.errors["assignedTeam"] !== undefined}
                    helperText={formState.errors["assignedTeam"]?.message}
                  />
                )}
                value={field.value}
                getOptionDisabled={(option) => !option.isMember && !option.isAdministrator}
                isOptionEqualToValue={(option, value) => option.slug === value.slug}
              />
            )}
          />
        </Stack>
        <Stack spacing={0.25} sx={{ width: "50%" }}>
          <SmallInputLabel>Reporting User</SmallInputLabel>
          <Controller
            name="reportingUser"
            control={control}
            render={({ field }) => (
              <UserAutocomplete
                placeholder="Select a person..."
                onChange={(value) => field.onChange(value)}
                value={field.value}
                autofocus
              />
            )}
          />
        </Stack>
      </Stack>

      <Accordion disableGutters={true} sx={{ width: "100%" }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="large" />}>
          <Typography>{ConnectObjectType.Metric} Setup</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EditMetricDatasets form={props.form} />
        </AccordionDetails>
      </Accordion>

      <MetricTable form={props.form} enableActions sx={{ maxHeight: 300 }} />
    </Stack>
  );
};

export { CreateEditMetricForm };
export type { ICreateEditMetricFormState, ICreateEditMetricFormStateData, ICreateEditMetricFormStateDataset };
